import { buildPageableParams, Page, PageableArgs } from '../common';
import { queryUntilLastPage } from '../common/pageable/query-until-last-page';
import { servicesApi } from '../services';
import {
  UtilisateurAdminDto,
  UtilisateurCreateDto,
  UtilisateurDto,
  UtilisateurProjection,
  UtilisateurRepresentantDelegataireDto,
  UtilisateurRole,
  UtilisateurUpdateDto,
} from '../utilisateur';

const ORGANISMES_URL_PREFIX = (id: number) => `organismes/${id}`;
const ORGANISMES_UTILISATEURS_URL_PREFIX = (id: number) => `${ORGANISMES_URL_PREFIX(id)}/utilisateurs`;

const organismeUtilisateurApi = servicesApi.injectEndpoints!({
  endpoints: (build) => ({

    addOrganismeUtilisateurSimple: build.mutation<void, CreateOrganismeUtilisateurSimpleArgs, any>({
      query: ({ id, utilisateurDto }) => ({
        url: `${ORGANISMES_URL_PREFIX(id)}/utilisateurs-simple`,
        method: 'POST',
        body: utilisateurDto,
      }),
      invalidatesTags: ['Organisme'],
    }),

    getOrganismeUtilisateurs: build.query<Page<UtilisateurDto>, GetOrganismeUtilisateursQueryArgs, any>({
      query: ({ id, ...pageable }) => ({
        url: ORGANISMES_UTILISATEURS_URL_PREFIX(id),
        params: buildPageableParams(pageable),
      }),
      providesTags: ['OrganismeUtilisateurs'],
    }),

    getAllOrganismeUtilisateurs: build.query<UtilisateurDto[], GetOrganismeUtilisateursQueryArgs, any>({
      queryFn: ({ id, ...pageable }, _api, _extraOptions, baseQuery) => queryUntilLastPage<UtilisateurDto>({
        baseQuery,
        url: ORGANISMES_UTILISATEURS_URL_PREFIX(id),
        params: buildPageableParams(pageable),
      }),
      providesTags: ['OrganismeUtilisateurs'],
    }),

    updateOrganismeUtilisateurSimple: build.mutation<void, UpdateOrganismeUtilisateurSimpleArgs, any>({
      query: ({ id, utilisateurId, utilisateurDto }) => ({
        url: `${ORGANISMES_URL_PREFIX(id)}/utilisateurs-simple/${utilisateurId}`,
        method: 'PATCH',
        body: utilisateurDto,
      }),
      invalidatesTags: ['Organisme'],
    }),

    deleteOrganismeUtilisateurSimple: build.mutation<void, DeleteOrganismeUtilisateurSimpleArgs, any>({
      query: ({ id, utilisateurId }) => ({
        url: `${ORGANISMES_URL_PREFIX(id)}/utilisateurs-simple/${utilisateurId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organisme'],
    }),

    updateOrganismeUtilisateursAdmin: build.mutation<void, UpdateOrganismeUtilisateurAdminArgs, any>({
      query: ({ id, utilisateursAdminDto }) => ({
        url: `${ORGANISMES_URL_PREFIX(id)}/utilisateurs-admins`,
        method: 'PATCH',
        body: utilisateursAdminDto,
      }),
      invalidatesTags: ['Organisme'],
    }),

    updateOrganismeUtilisateursRepresentantDelegataire:
      build.mutation<void, UpdateOrganismeUtilisateursRepresentantDelegataireArgs, any>({
        query: ({ id, utilisateursRepresentantDelegataireDto }) => ({
          url: `${ORGANISMES_URL_PREFIX(id)}/utilisateurs-representant-delegataire`,
          method: 'PATCH',
          body: utilisateursRepresentantDelegataireDto,
        }),
        invalidatesTags: ['Organisme', 'OrganismeFichiers'],
      }),
  }),
});

export interface GetOrganismeUtilisateursQueryArgs extends PageableArgs<UtilisateurProjection> {
  id: number;
}

export interface CreateOrganismeUtilisateurSimpleArgs {
  id: number;
  utilisateurDto: UtilisateurCreateDto;
}

export interface UpdateOrganismeUtilisateurSimpleArgs {
  id: number;
  utilisateurId: number;
  utilisateurDto: UtilisateurUpdateDto;
}

export interface DeleteOrganismeUtilisateurSimpleArgs {
  id: number;
  utilisateurId: number;
  utilisateurRole: UtilisateurRole;
}

export interface UpdateOrganismeUtilisateurAdminArgs {
  id: number,
  utilisateursAdminDto: UtilisateursAdminDto
}

export interface UtilisateursAdminDto {
  admin1: UtilisateurAdminDto,
  admin2?: UtilisateurAdminDto,
}

export interface UpdateOrganismeUtilisateursRepresentantDelegataireArgs {
  id: number,
  utilisateursRepresentantDelegataireDto: UtilisateursRepresentantDelegataireDtoDto
}

export interface UtilisateursRepresentantDelegataireDtoDto {
  representant: UtilisateurRepresentantDelegataireDto,
  delegataire?: UtilisateurRepresentantDelegataireDto,
}

export const {
  useLazyGetOrganismeUtilisateursQuery,
  useGetAllOrganismeUtilisateursQuery,
  useAddOrganismeUtilisateurSimpleMutation,
  useUpdateOrganismeUtilisateurSimpleMutation,
  useDeleteOrganismeUtilisateurSimpleMutation,
  useUpdateOrganismeUtilisateursAdminMutation,
  useUpdateOrganismeUtilisateursRepresentantDelegataireMutation,
} = organismeUtilisateurApi;